import { useEffect, useSyncExternalStore } from 'react';
import { api } from '../../lib/api.ts';

/**
 * Who else has a chat open right now (v1.84.0).
 *
 * "แสดงสถานะ ชื่อแอดมิน เช่น NATASHU กำลังทำงาน.. จนกว่าจะออกหรือเปลี่ยนช่องแชทนั้น" — with one condition the
 * owner put first: "ทำแบบไม่กินทรัพยากรนะผมห่วงมาก". So:
 *  - one small POST when a chat opens, then a beat every 25 s while it stays open, and a last one on the way out;
 *  - the answer comes down the realtime stream that is already open, as a frame the server never stores;
 *  - names are held in this module, not in the query cache, so a change repaints the rows and nothing refetches.
 */
export interface ChatViewer {
  userId: string;
  displayName: string;
}

/** Matches the server's TTL of 60 s: a beat every 25 s survives one miss. */
const BEAT_MS = 25_000;

const rooms = new Map<string, ChatViewer[]>();
const listeners = new Set<() => void>();
let snapshotVersion = 0;

const EMPTY: ChatViewer[] = [];

function publish(): void {
  snapshotVersion += 1;
  for (const l of listeners) l();
}

/** Called by the realtime stream when a chat's set of viewers changes. */
export function applyViewers(conversationId: string, viewers: ChatViewer[]): void {
  if (viewers.length === 0) rooms.delete(conversationId);
  else rooms.set(conversationId, viewers);
  publish();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Everyone in a chat, including whoever is asking — the caller decides whether to hide itself. */
export function viewersOf(conversationId: string): ChatViewer[] {
  return rooms.get(conversationId) ?? EMPTY;
}

/** Re-renders when any chat's viewers change; reading is a plain map lookup. */
export function useViewersVersion(): number {
  return useSyncExternalStore(
    subscribe,
    () => snapshotVersion,
    () => snapshotVersion,
  );
}

/**
 * Tells the server which chat this admin is looking at, and keeps saying so until they leave.
 *
 * Passing `null` (no chat open) sends one message to clear the seat and then stays quiet — an admin sitting on
 * the list costs nothing at all.
 */
export function useAnnounceViewing(conversationId: string | null): void {
  useEffect(() => {
    let alive = true;
    const beat = (changed: boolean) => {
      if (!alive) return;
      void api.post('/api/conversations/viewing', { conversationId, changed }).catch(() => undefined);
    };
    beat(true);
    if (!conversationId) return () => {
      alive = false;
    };

    const timer = window.setInterval(() => {
      // A background tab is not somebody working in the chat; its seat expires and comes back on return.
      if (document.visibilityState === 'visible') beat(false);
    }, BEAT_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') beat(false);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      // Leaving is worth one request even while the page is unloading. Without a beat the seat expires on
      // its own within the minute, so a lost request only delays the name going away.
      void api.post('/api/conversations/viewing', { conversationId: null }).catch(() => undefined);
    };
  }, [conversationId]);
}
